import { useCallback, useMemo, useState } from 'react';
import type { GetOptionKey, GetOptionLabel, GetOptionDisabled } from './types';

export interface UseAutocompleteOptions<T> {
  value: T | T[] | string | (T | string)[] | null;
  onChange: (next: T | T[] | string | (T | string)[] | null) => void;
  items: T[];
  multiple?: boolean;
  allowFreeText?: boolean;
  locked?: boolean;
  onUnlock?: () => void;
  getOptionKey: GetOptionKey<T>;
  getOptionLabel?: GetOptionLabel<T>;
  getOptionDisabled?: GetOptionDisabled<T>;
  query?: string;
  onQueryChange?: (q: string) => void;
}

export interface UseAutocompleteResult<T> {
  query: string;
  setQuery: (q: string) => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  moveActive: (delta: 1 | -1 | 'first' | 'last') => void;
  commitActive: () => void;
  commitFreeText: () => void;
  isSelected: (option: T) => boolean;
  select: (option: T) => void;
  remove: (option: T) => void;
}

const NOOP_DISABLED = () => false;

export function useAutocomplete<T>(opts: UseAutocompleteOptions<T>): UseAutocompleteResult<T> {
  const {
    value,
    onChange,
    items,
    getOptionKey,
    getOptionDisabled = NOOP_DISABLED,
    multiple = false,
    locked = false,
    onUnlock,
    allowFreeText = false,
  } = opts;

  const [internalQuery, setInternalQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const query = opts.query ?? internalQuery;
  const writeQuery = useCallback(
    (q: string) => {
      if (opts.onQueryChange) opts.onQueryChange(q);
      else setInternalQuery(q);
    },
    [opts],
  );

  const selectedKeys = useMemo(() => {
    if (value == null) return new Set<string>();
    const arr = Array.isArray(value) ? value : [value];
    return new Set(arr.filter((v): v is T => typeof v !== 'string').map(getOptionKey));
  }, [value, getOptionKey]);

  const isSelected = useCallback(
    (option: T) => selectedKeys.has(getOptionKey(option)),
    [selectedKeys, getOptionKey],
  );

  const open = useCallback(() => {
    if (locked) return;
    setIsOpen(true);
  }, [locked]);
  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(0);
  }, []);

  const setQuery = useCallback(
    (q: string) => {
      if (locked) return;
      writeQuery(q);
      setIsOpen(q.length > 0);
      setActiveIndex(0);
    },
    [writeQuery, locked],
  );

  const findNextEnabled = useCallback(
    (start: number, direction: 1 | -1): number => {
      if (items.length === 0) return 0;
      let i = start;
      for (let n = 0; n < items.length; n++) {
        i = (i + direction + items.length) % items.length;
        if (!getOptionDisabled(items[i])) return i;
      }
      return start;
    },
    [items, getOptionDisabled],
  );

  const moveActive = useCallback(
    (delta: 1 | -1 | 'first' | 'last') => {
      if (items.length === 0) return;
      if (delta === 'first') {
        setActiveIndex(getOptionDisabled(items[0]) ? findNextEnabled(0, 1) : 0);
        return;
      }
      if (delta === 'last') {
        const last = items.length - 1;
        setActiveIndex(getOptionDisabled(items[last]) ? findNextEnabled(last, -1) : last);
        return;
      }
      setActiveIndex((idx) => findNextEnabled(idx, delta));
    },
    [items, getOptionDisabled, findNextEnabled],
  );

  const select = useCallback(
    (option: T) => {
      if (locked) {
        onUnlock?.();
        return;
      }
      if (getOptionDisabled(option)) return;
      if (multiple) {
        const current = (Array.isArray(value) ? value : []) as (T | string)[];
        const key = getOptionKey(option);
        const already = current.some((v) => typeof v !== 'string' && getOptionKey(v) === key);
        const next = already
          ? current.filter((v) => typeof v === 'string' || getOptionKey(v) !== key)
          : [...current, option];
        onChange(next as T[]);
        writeQuery('');
        setActiveIndex(0);
      } else {
        onChange(option);
        setIsOpen(false);
        writeQuery('');
        setActiveIndex(0);
      }
    },
    [onChange, getOptionDisabled, multiple, value, getOptionKey, writeQuery, locked, onUnlock],
  );

  const remove = useCallback(
    (option: T) => {
      if (locked) {
        onUnlock?.();
        return;
      }
      const key = getOptionKey(option);
      if (multiple) {
        const current = (Array.isArray(value) ? value : []) as (T | string)[];
        onChange(current.filter((v) => typeof v === 'string' || getOptionKey(v) !== key) as T[]);
      } else {
        onChange(null);
      }
    },
    [onChange, multiple, value, getOptionKey, locked, onUnlock],
  );

  const commitActive = useCallback(() => {
    const option = items[activeIndex];
    if (option) select(option);
  }, [items, activeIndex, select]);

  const commitFreeText = useCallback(() => {
    if (locked) {
      onUnlock?.();
      return;
    }
    if (!allowFreeText) return;
    const trimmed = query.trim();
    if (trimmed.length === 0) return;
    if (multiple) {
      const current = (Array.isArray(value) ? value : []) as (T | string)[];
      onChange([...current, trimmed]);
    } else {
      onChange(trimmed);
    }
    writeQuery('');
    setIsOpen(false);
    setActiveIndex(0);
  }, [allowFreeText, locked, onUnlock, multiple, query, value, onChange, writeQuery]);

  return {
    query,
    setQuery,
    isOpen,
    open,
    close,
    activeIndex,
    setActiveIndex,
    moveActive,
    commitActive,
    commitFreeText,
    isSelected,
    select,
    remove,
  };
}
